// ============================================================================
// PRELIT-ESC-001 — Escalate Case
// ============================================================================
// Medium modal. Target is locked to the trigger button clicked
// (ARBITRATION or SARFAESI). SARFAESI gated by is_secured=TRUE.
// Notes required, min 20 chars (auditor pressure).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useEscalateCase } from 'hooks/api/usePreLitigation';
import { useClosureReasons } from 'hooks/api/useClosureReasons';
import type { EscalationTarget } from 'api/types/preLitigation.types';

interface FormState {
  closureReasonId: string;
  notes: string;
}

interface EscalateCaseModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  /** ARBITRATION or SARFAESI — passed by the action bar trigger that opened the modal. */
  targetType: EscalationTarget;
  /** Whether the underlying account has security. SARFAESI escalation is blocked when false. */
  isSecured: boolean;
  /** Live current outstanding for the audit confirmation chip. */
  currentOutstanding: number;
  onClose: () => void;
  onEscalated: () => void;
}

const buildInitial = (): FormState => ({ closureReasonId: '', notes: '' });

const EscalateCaseModal: React.FC<EscalateCaseModalProps> = ({
  isOpen, caseId, caseCode, targetType, isSecured, currentOutstanding, onClose, onEscalated,
}) => {
  const [form, setForm] = useState<FormState>(buildInitial());
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const escalate = useEscalateCase();
  const { data: closureReasons } = useClosureReasons({ page: 1, pageSize: 200 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial();
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => snapRef.current && JSON.stringify(form) !== snapRef.current, [form]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
    setForm(p => ({ ...p, [field]: value }));
  };

  // Escalation closure reasons are NEUTRAL (escalation isn't favourable or
  // unfavourable from pre-lit's perspective — the parent flow takes over).
  const closureReasonOptions = useMemo(
    () => (closureReasons ?? [])
      .filter((r: any) => r.outcomeCategory === 'NEUTRAL')
      .map((r: any) => ({ value: String(r.id), label: r.closureReasonName ?? r.closureCode ?? String(r.id) })),
    [closureReasons],
  );

  const sarfaesiBlocked = targetType === 'SARFAESI' && !isSecured;

  const handleClose = useCallback(() => {
    if (escalate.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, escalate.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (sarfaesiBlocked) {
      errors.notes = 'SARFAESI requires a secured asset on the account.';
    }
    if (!form.closureReasonId) errors.closureReasonId = 'Pick an escalation reason.';
    if (!form.notes || form.notes.trim().length < 20) {
      errors.notes = 'Notes are required (min 20 chars) — auditors will read this.';
    }
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, sarfaesiBlocked]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      await escalate.run(caseId, {
        targetType,
        closureReasonId: form.closureReasonId,
        notes: form.notes.trim(),
      });
      notificationService.toast.success(`${caseCode} escalated to ${targetType}.`);
      onEscalated();
      onClose();
    } catch (err: any) {
      const m = err?.response?.data?.message ?? 'Failed to escalate.';
      notificationService.toast.error(m, 6000);
    }
  }, [escalate, caseId, caseCode, targetType, form, onEscalated, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {escalate.loading && <LoadingOverlay text="Escalating..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Escalate Case — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={escalate.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Confirmation chip */}
          <div style={{
            padding: '0.75rem 1rem',
            background: sarfaesiBlocked ? '#fee2e2' : 'var(--bg-secondary)',
            color: sarfaesiBlocked ? '#991b1b' : 'var(--text-primary)',
            border: sarfaesiBlocked ? '1px solid #f87171' : '1px solid var(--border-color)',
            borderRadius: '6px', marginBottom: '1rem',
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: sarfaesiBlocked ? '#991b1b' : 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Escalating to <strong>{targetType}</strong>
            </div>
            {sarfaesiBlocked ? (
              <div style={{ fontSize: '0.85rem' }}>
                ⛔ SARFAESI requires a secured asset. This account is unsecured — escalate to ARBITRATION instead, or add security first.
              </div>
            ) : (
              <div style={{ fontSize: '0.8rem' }}>
                <span><strong>Outstanding:</strong> ₹{currentOutstanding.toLocaleString('en-IN')}</span>
                {' · '}
                <span><strong>Secured:</strong> {isSecured ? 'Yes' : 'No'}</span>
              </div>
            )}
          </div>

          <div className="form-group" data-field="closureReasonId">
            <SyutSelect
              label={<>Closure Reason <span className="text-danger">*</span></>}
              options={closureReasonOptions}
              value={form.closureReasonId}
              onChange={(v) => setField('closureReasonId', (v as string) || '')}
              error={fieldErrors.closureReasonId}
              placeholder="Pick a neutral reason..."
            />
            {closureReasonOptions.length === 0 && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                No neutral closure reasons configured. Add one in Master Data → Closure Reasons.
              </div>
            )}
          </div>

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes <span className="text-danger">*</span> <span style={{ color: 'var(--text-muted)' }}>(min 20 chars)</span></label>
            <textarea
              className={`form-textarea${fieldErrors.notes ? ' form-field-error' : ''}`}
              rows={5}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Why is this case being escalated to the next stage?"
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              {form.notes.length} / 2000
            </div>
            {fieldErrors.notes && <div className="form-error-message">{fieldErrors.notes}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={escalate.loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={escalate.loading || sarfaesiBlocked}>
            Escalate to {targetType}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Escalation"
        message={`Escalate case ${caseCode} to ${targetType}? This closes the pre-litigation case.`}
        confirmLabel={`Escalate to ${targetType}`}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default EscalateCaseModal;
